import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from "@mui/material";

import { useDebounce } from "src/reusable/hooks/useDebounce";
import { useListParts, type PartListItem } from "../services/PartService";
import { useListPartGroups } from "../services/PartGroupService";
import PartCardList from "../dashboard/PartCardList";

type PartPickerDialogProps = {
  open: boolean;
  title: string;
  onClose: () => void;
  onSelect: (part: PartListItem) => void;
  /** The part held by the OTHER slot, hidden here so one part cannot fill both. */
  excludePartId?: number | null;
};

const PAGE_SIZE = 12;

/**
 * Modal part picker used by the comparison splash to choose / change the
 * installed (original) and candidate (replacement) parts. Reuses the
 * dashboard's `useListParts` + `PartCardList` so the search,
 * paging, and card rendering match the rest of Parts AI.
 */
const PartPickerDialog = ({
  open,
  title,
  onClose,
  onSelect,
  excludePartId,
}: PartPickerDialogProps) => {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [groupFilter, setGroupFilter] = useState<number | "">("");
  const debouncedSearch = useDebounce(search, 300);

  const { data: groups = [] } = useListPartGroups();
  const { data, isLoading } = useListParts({
    search: debouncedSearch || undefined,
    part_group_fk: groupFilter === "" ? undefined : groupFilter,
    exclude_part_id: excludePartId ?? undefined,
    page,
    page_size: PAGE_SIZE,
  });

  // exclude_part_id lands with MRE #30789; until that merges the backend
  // ignores the unknown param and this filter is the only exclusion, with
  // the paginated total reading one high. Once it merges, the query excludes
  // and counts in one place and this filter becomes a no-op fallback.
  const selectable = useMemo(() => {
    if (!data || excludePartId == null) return data;
    return { ...data, items: data.items.filter((p) => p.id !== excludePartId) };
  }, [data, excludePartId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          <TextField
            select
            size="small"
            label="Part group"
            value={groupFilter}
            onChange={(e) => {
              setGroupFilter(
                e.target.value === "" ? "" : Number(e.target.value),
              );
              setPage(1);
            }}
            sx={{ minWidth: 220 }}
            data-testid="parts-ai-comparison-group-filter"
          >
            <MenuItem value="">All groups</MenuItem>
            {groups.map((group) => (
              <MenuItem key={group.id} value={group.id}>
                {group.name}
                {typeof group.part_count === "number"
                  ? ` (${group.part_count})`
                  : ""}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            placeholder="Search parts…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            data-testid="parts-ai-comparison-part-search"
          />
        </Stack>
        <PartCardList
          data={selectable}
          isLoading={isLoading}
          page={page}
          onPageChange={setPage}
          onPartClick={(part) => {
            onSelect(part);
            onClose();
          }}
          emptyMessage={
            // The client-side exclusion emptied a page the backend counted:
            // parts exist, this page just held only the other slot's part.
            data && data.items.length > 0 && selectable?.items.length === 0
              ? "The only part on this page is already selected in the other slot. Try another page or refine the search."
              : undefined
          }
        />
      </DialogContent>
    </Dialog>
  );
};

export default PartPickerDialog;
